import { useContext, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { DomainCatalogContext, useResourceApi } from './ResourceApi';
import { Select, type SelectProps } from './Select';
import type { Domain } from './types';

export function DomainSelect({ domains, children, endpoint, ...props }: SelectProps & { domains: Domain[]; endpoint?: string }) {
  const prefix = useResourceApi();
  const remember = useContext(DomainCatalogContext);
  const [items, setItems] = useState<Domain[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const query = useRef('');
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  const load = async (search: string, next?: string) => {
    request.current?.abort(); const controller = new AbortController(); request.current = controller;
    query.current = search; setLoading(true); setError('');
    if (!next) setCursor(null);
    try {
      const params = new URLSearchParams({ limit: '25', search, ...(next ? { cursor: next } : {}) });
      const result = await api<{ items: Domain[]; nextCursor: string | null }>(`${endpoint ?? `${prefix}/domains`}?${params}`, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setItems((current) => next ? [...(current ?? []), ...result.items.filter((item) => !current?.some((existing) => existing.id === item.id))] : result.items);
      setCursor(result.nextCursor); remember(result.items);
    } catch (reason) { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : '加载失败'); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  };
  const selected = domains.find((domain) => domain.id === props.value);
  const options = items ?? domains;
  const visible = selected && !options.some((domain) => domain.id === selected.id) ? [selected, ...options] : options;
  return <Select {...props} loading={loading} loadError={error} onSearch={(search) => void load(search)} onLoadMore={cursor ? () => void load(query.current, cursor) : undefined}>
    {children}
    {visible.map((domain) => <option key={domain.id} value={domain.id} data-search={`${domain.ipv4 ?? ''} ${domain.ipv6 ?? ''}`}>{domain.display_name || domain.label} · {domain.fqdn}</option>)}
  </Select>;
}
